import { useState, useEffect, useCallback } from 'react'
import { useCurrentUser } from './AuthHooks'
import { useUpdateProfileMutation } from 'apis/UserApi'
import { useDispatch } from 'react-redux'
import { updateCurrentUser } from 'store/AuthSlice'
import { extractPhoneDigits, arePhoneNumbersEqual } from 'utils/phoneUtils'
import {
  Address,
  AddressType,
  BillingAddress,
  DeliveryAddress,
} from 'models/Address'
import { newId } from 'services'
import { StoredPaymentMethod } from 'models/PaymentMethod'

export enum UpdateStatus {
  IDLE = 'idle',
  SAVING = 'saving',
  SUCCESS = 'success',
  ERROR = 'error',
}

interface UserProfileForm {
  firstName?: string
  lastName?: string
  email?: string
  phoneNumber?: string
  billingAddresses?: BillingAddress[]
  deliveryAddresses?: DeliveryAddress[]
  paymentMethods?: StoredPaymentMethod[]
}

export const useUserProfile = () => {
  const { currentUser } = useCurrentUser()
  const [formData, setFormData] = useState<UserProfileForm>({
    firstName: currentUser?.firstName,
    lastName: currentUser?.lastName,
    email: currentUser?.email,
    phoneNumber: currentUser?.phoneNumber,
    billingAddresses: currentUser?.billingAddresses ?? [],
    deliveryAddresses: currentUser?.deliveryAddresses ?? [],
    paymentMethods: currentUser?.paymentMethods ?? [],
  })
  const [isSaving, setIsSaving] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [updateProfile] = useUpdateProfileMutation()
  const dispatch = useDispatch()
  const [updateStatus, setUpdateStatus] = useState<UpdateStatus>(
    UpdateStatus.IDLE,
  )

  useEffect(() => {
    const isFormDirty =
      formData.firstName !== currentUser?.firstName ||
      formData.lastName !== currentUser?.lastName ||
      !arePhoneNumbersEqual(formData.phoneNumber, currentUser?.phoneNumber)

    setIsDirty(isFormDirty)
  }, [formData, currentUser])

  const handleInputChange = (field: keyof UserProfileForm, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSaveChanges = async () => {
    if (!isDirty) return

    try {
      setIsSaving(true)
      setUpdateStatus(UpdateStatus.SAVING)

      const updatedFields: Partial<UserProfileForm> = {}

      if (formData.firstName !== currentUser?.firstName) {
        updatedFields.firstName = formData.firstName
      }

      if (formData.lastName !== currentUser?.lastName) {
        updatedFields.lastName = formData.lastName
      }

      if (
        !arePhoneNumbersEqual(formData.phoneNumber, currentUser?.phoneNumber)
      ) {
        updatedFields.phoneNumber =
          extractPhoneDigits(formData.phoneNumber) || undefined
      }

      if (Object.keys(updatedFields).length > 0) {
        await updateProfile(updatedFields).unwrap()

        dispatch(updateCurrentUser(updatedFields))

        setIsDirty(false)
        setUpdateStatus(UpdateStatus.SUCCESS)
      }
    } catch (error) {
      console.error('Failed to save profile:', error)
      setUpdateStatus(UpdateStatus.ERROR)
    } finally {
      setIsSaving(false)
    }
  }

  const getAddressesKey = (
    type: AddressType,
  ): 'billingAddresses' | 'deliveryAddresses' => {
    return type === AddressType.Billing
      ? 'billingAddresses'
      : 'deliveryAddresses'
  }

  const ensureOneDefaultAddress = (addresses: Address[]): Address[] => {
    if (addresses.length === 0) return addresses

    const hasDefault = addresses.some((addr) => addr.default)

    if (!hasDefault && addresses.length > 0) {
      return addresses.map((addr, index) => ({
        ...addr,
        default: index === 0,
      }))
    }

    return addresses
  }

  const saveAddress = useCallback(
    async (address: Address, type: AddressType): Promise<void> => {
      if (!currentUser) {
        return
      }

      try {
        setUpdateStatus(UpdateStatus.SAVING)

        const addressesKey = getAddressesKey(type)
        let updatedAddresses = [...(currentUser[addressesKey] || [])]

        if (!address.id) {
          address.id = newId()
          updatedAddresses.push(address)
        } else {
          updatedAddresses = updatedAddresses.map((addr) =>
            addr.id === address.id ? address : addr,
          )
        }

        if (address.default) {
          updatedAddresses = updatedAddresses.map((addr) => ({
            ...addr,
            default: addr.id === address.id,
          }))
        } else {
          updatedAddresses = ensureOneDefaultAddress(updatedAddresses)
        }

        const updatePayload = { [addressesKey]: updatedAddresses }
        await updateProfile(updatePayload).unwrap()

        dispatch(updateCurrentUser(updatePayload))

        setFormData((prev) => ({
          ...prev,
          [addressesKey]: updatedAddresses,
        }))

        setUpdateStatus(UpdateStatus.SUCCESS)
        return Promise.resolve()
      } catch (error) {
        console.error(`Error saving ${type} address:`, error)
        setUpdateStatus(UpdateStatus.ERROR)
        return Promise.reject(error)
      }
    },
    [currentUser, dispatch, updateProfile],
  )

  const deleteAddress = useCallback(
    async (addressId: string | undefined, type: AddressType): Promise<void> => {
      if (!currentUser || !addressId) {
        return
      }

      try {
        setUpdateStatus(UpdateStatus.SAVING)

        const addressesKey = getAddressesKey(type)
        let updatedAddresses = (currentUser[addressesKey] || []).filter(
          (addr) => addr.id !== addressId,
        )

        updatedAddresses = ensureOneDefaultAddress(updatedAddresses)

        const updatePayload = { [addressesKey]: updatedAddresses }
        await updateProfile(updatePayload).unwrap()

        dispatch(updateCurrentUser(updatePayload))

        setFormData((prev) => ({
          ...prev,
          [addressesKey]: updatedAddresses,
        }))

        setUpdateStatus(UpdateStatus.SUCCESS)
        return Promise.resolve()
      } catch (error) {
        console.error(`Error deleting ${type} address:`, error)
        setUpdateStatus(UpdateStatus.ERROR)
        return Promise.reject(error)
      }
    },
    [currentUser, dispatch, updateProfile],
  )

  const saveBillingAddress = useCallback(
    async (address: BillingAddress): Promise<void> => {
      return saveAddress(address, AddressType.Billing)
    },
    [saveAddress],
  )

  const deleteBillingAddress = useCallback(
    async (addressId?: string): Promise<void> => {
      return deleteAddress(addressId, AddressType.Billing)
    },
    [deleteAddress],
  )

  const saveDeliveryAddress = useCallback(
    async (address: DeliveryAddress): Promise<void> => {
      return saveAddress(address, AddressType.Delivery)
    },
    [saveAddress],
  )

  const deleteDeliveryAddress = useCallback(
    async (addressId?: string): Promise<void> => {
      return deleteAddress(addressId, AddressType.Delivery)
    },
    [deleteAddress],
  )

  // Simple function to reset status
  const resetStatus = useCallback(() => {
    setUpdateStatus(UpdateStatus.IDLE)
  }, [])

  return {
    formData,
    isSaving,
    isDirty,
    updateStatus,
    resetStatus,
    handleInputChange,
    handleSaveChanges,
    saveBillingAddress,
    deleteBillingAddress,
    saveDeliveryAddress,
    deleteDeliveryAddress,
  }
}
